// @givmo/org-console — a SERVED setting definition, rendered as a knob card (Org Machinery OM-17,
// seam A).
//
// The server serves the definition (`OrgSettingDefinition`: key, kind, choices with their labels,
// default, label, description, placeholder, its permission string) beside the organization's stored
// value and its state, and this renders exactly that: a `Switch` for a boolean, a `Select` for a
// choice, a short text field for a text — autosaving per control, the way the preferences panel
// saves a preference. The page hard-codes NO setting; a definition the backend does not serve is a
// card that does not exist. ONE card shape for every definition, in a module's section and in the
// Organization section alike (the paradigm law).
//
// ══ THE MOCKUP PASS'S PINNED BUILD REQUIREMENTS (OM-17 Mechanics) ══════════════════════════════
//
//   (1) nothing in `CardHeader`'s `shrink-0` actions slot — a badge there squeezes the title to
//       "Co…" at 390. This card puts NOTHING in that slot.
//   (2) the title WRAPS. The design system's `CardHeader` truncates its `title` prop
//       ("…open Donati…" at 390), so the header renders its own wrapping `h3` as a child.
//   (4) the "Saving… / Saved." live region is `sr-only` while idle — no reserved dead space under
//       a card. Only what is true takes space: the last-change line once there is a change, and a
//       refusal (a 403, a transport failure, a stale view).
//
// ══ EVERY CHANGE IS MADE FROM A REVISION (design 4A §8 item 4, §11 item 6 — RULED) ═════════════
//
// The write is PARTIAL and THREE-VALUED per key (`{values: {[key]: value}}`) and names the revision
// the person saw (`expected_revisions`, 0 for never set). A key changed since is refused — 409
// `SETTING_STALE`, nothing saved — and the card says the line the server serves with the refusal
// (D-CCM-44's approved words; the web keeps no copy), after the page has re-read the setting, so the
// control shows what the record now says. Like the AI card's (`@givmo/compliance`), the line is said
// only once that re-read has answered and only while the record does not hold what was asked: two
// people choosing the same month moments apart need no line, and making the change again would undo
// nothing. While a change is in flight the control shows it and holds still.
//
// ══ WHO LAST CHANGED IT (§11 item 6 — RULED) ═══════════════════════════════════════════════════
//
// "Changed by <name>, <date>" from the served `state` and the roster the console already reads —
// the composer names the person; a change through the person's connected AI names it as D-CCM-96
// does. Never changed: no line. The line waits for the roster to settle rather than flash a stand-in.
//
// The unset control says the definition's served placeholder ("Choose a month"); there is no
// selectable "Not set" option (§7, RULED). A text field commits on blur or Enter when its value
// changed, never per keystroke, and its words are the person's until the RECORD moves: a save that
// fails for any reason but a stale view leaves them in the field, so making the change again is one
// key. A switch and a select snap back to the record instead — one click remakes the choice.
//
// ══ FACTS (OM-64, Design 4C §8; the frames approved by D-CPO-69) ══════════════════════════════
//
// A definition the server marks a FACT renders under Organization details in the same card shape,
// in one of two modes. EDITABLE (the served `can_change`): a choice keeps the `Select` above,
// unchanged; an address is its own body (`settings-address-card.tsx`). READ-ONLY (`can_change` false
// — a person whose app reads the fact, but who may not change it, M10): the value as text, the
// last-change line, and one line saying who can change it ("A Global Admin can change this."; OM-61
// replaces it with Design 4B's refusal and request).
//
// DATA CONTRACT: the composer's `<prefix>-def-<key>` (the card) · `-control` · `-value` ·
// `-last-change` · `-read-only` · `-status` · `-error`.

import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import {
  Card,
  CardBody,
  ErrorMessage,
  Select,
  Switch,
  TextField,
  testIdProp,
} from '@givmo/design-system';
import { decodeOrgAddress } from './org-decode';
import { isOrgSettingStale } from './org-console-transport';
import {
  OrgAddressFactCard,
  addressValueLines,
  type OrgAddressSameAs,
} from './settings-address-card';
import {
  CardHintLine,
  DefinitionHeader,
  FACT_READ_ONLY_LINE,
  FactValue,
  SETTING_SAVE_FAILED_LINE,
  lastChangeFor,
} from './settings-card-parts';
import { useUpdateOrgSettings } from './use-org-console';
import type { OrgSettingDefinition, OrgSettingState } from './wire-types';

export { SETTING_SAVE_FAILED_LINE, settingChangeLine } from './settings-card-parts';

export interface OrgSettingDefinitionCardProps {
  orgRef: string;
  definition: OrgSettingDefinition;
  /** The organization's STORED value for this key, or `undefined` when it is at its default. */
  stored: unknown;
  /** The key's served state, or `undefined` when it was never set (revision 0). */
  state: OrgSettingState | undefined;
  /** Whether the page's settings read is re-reading — a refusal's line waits for its answer. */
  refreshing: boolean;
  /**
   * Names a user id from the roster, or `undefined` for a person it does not hold. Absent until the
   * roster read has settled, so the last-change line never flashes a stand-in.
   */
  personName?: (userId: string) => string | undefined;
  /** The card's testid — the composer's `org-settings-<section>-def-<key>`. */
  testId: string;
  /**
   * Whether THIS person may change it (Design 4C §4): the served `can_change`, which the composer
   * resolves for an older backend's body from `requires`. `false` draws the read-only mode.
   */
  canChange?: boolean;
  /** For an address: the outside record's mailing address the card may copy (D-CPO-61). */
  sameAs?: OrgAddressSameAs | null;
  /** For an address: the page's unsaved-changes guard, for the open form. */
  registerDirty?: (dirty: boolean) => void;
}

/** ONE card per served definition: a knob, a kept address, or a read-only fact. */
export function OrgSettingDefinitionCard(props: OrgSettingDefinitionCardProps) {
  const { canChange = true, definition } = props;
  if (!canChange) return <ReadOnlyFactCard {...props} />;
  if (definition.kind === 'address') return <OrgAddressFactCard {...props} />;
  return <KnobCard {...props} />;
}

/**
 * The value as text, for a person who may read it but not change it — or `null` when it is not set.
 * A choice reads as its served label, never as its placeholder: "Choose a state" asks the reader to
 * do something they cannot.
 */
function readOnlyLines(
  definition: OrgSettingDefinition,
  stored: unknown,
  sameAs: OrgAddressSameAs | null,
): string[] | null {
  const value = stored === undefined ? definition.default : stored;
  if (definition.kind === 'address') return addressValueLines(decodeOrgAddress(value), sameAs);
  if (definition.kind === 'choice') {
    const choice = definition.choices.find((candidate) => candidate.value === value);
    return choice === undefined ? null : [choice.label];
  }
  if (definition.kind === 'boolean') return [value === true ? 'On' : 'Off'];
  return typeof value === 'string' && value.length > 0 ? [value] : null;
}

function ReadOnlyFactCard({
  definition,
  stored,
  state,
  personName,
  sameAs = null,
  testId,
}: OrgSettingDefinitionCardProps) {
  const headingId = useId();
  const lastChange = lastChangeFor(state, personName);
  return (
    <Card {...testIdProp(testId)}>
      <DefinitionHeader definition={definition} headingId={headingId} />
      <CardBody className="flex flex-col gap-8">
        <FactValue lines={readOnlyLines(definition, stored, sameAs)} testId={`${testId}-value`} />
        {lastChange !== null && (
          <CardHintLine testId={`${testId}-last-change`}>{lastChange}</CardHintLine>
        )}
        <CardHintLine testId={`${testId}-read-only`}>{FACT_READ_ONLY_LINE}</CardHintLine>
      </CardBody>
    </Card>
  );
}

function KnobCard({
  orgRef,
  definition,
  stored,
  state,
  refreshing,
  personName,
  testId,
}: OrgSettingDefinitionCardProps) {
  const update = useUpdateOrgSettings(orgRef);
  const [saved, setSaved] = useState(false);
  const headingId = useId();
  const value = stored === undefined ? definition.default : stored;
  const asked = update.variables?.values[definition.key];
  // While a change is in flight a switch or a select shows it; the answer (or the re-read) then
  // replaces it. A text field needs neither: its draft IS the change (`TextSetting`).
  const shown = update.isPending ? asked : value;

  const write = (next: unknown) => {
    setSaved(false);
    update.mutate(
      {
        values: { [definition.key]: next },
        expected_revisions: { [definition.key]: state?.revision ?? 0 },
      },
      { onSuccess: () => setSaved(true) },
    );
  };

  // Said only once the re-read has answered, and only while the record does not hold what was asked.
  const failed = update.isError && !refreshing && value !== asked;
  const failureLine =
    isOrgSettingStale(update.error) && update.error !== null && update.error.message.trim() !== ''
      ? update.error.message
      : SETTING_SAVE_FAILED_LINE;
  const lastChange = lastChangeFor(state, personName);

  return (
    <Card {...testIdProp(testId)}>
      <DefinitionHeader definition={definition} headingId={headingId} />
      <CardBody className="flex flex-col gap-8">
        {definition.kind === 'boolean' ? (
          <Switch
            appearance="saas"
            checked={shown === true}
            disabled={update.isPending}
            onCheckedChange={(checked) => write(checked)}
            aria-labelledby={headingId}
            testId={`${testId}-control`}
          />
        ) : definition.kind === 'choice' ? (
          <Select
            appearance="saas"
            aria-label={definition.label}
            options={definition.choices.map((choice) => ({
              value: choice.value,
              label: choice.label,
            }))}
            value={typeof shown === 'string' ? shown : null}
            placeholder={definition.placeholder ?? undefined}
            disabled={update.isPending}
            onValueChange={(next) => {
              if (next === null || next === value) return;
              write(next);
            }}
            className="max-w-[420px]"
            testId={`${testId}-control`}
          />
        ) : (
          <TextSetting
            label={definition.label}
            value={typeof value === 'string' ? value : ''}
            placeholder={definition.placeholder ?? undefined}
            disabled={update.isPending}
            onCommit={write}
            testId={`${testId}-control`}
          />
        )}
        {/* A refusal answers the act, so it sits under the control; who last changed the setting
            follows — after a stale refusal, it says who moved it. */}
        {failed && <ErrorMessage testId={`${testId}-error`}>{failureLine}</ErrorMessage>}
        {lastChange !== null && (
          // The page's one style for a line under a select (the Landing card's hint).
          <CardHintLine testId={`${testId}-last-change`}>{lastChange}</CardHintLine>
        )}
        <p role="status" aria-live="polite" className="sr-only" {...testIdProp(`${testId}-status`)}>
          {update.isPending ? 'Saving…' : saved ? 'Saved.' : ''}
        </p>
      </CardBody>
    </Card>
  );
}

interface TextSettingProps {
  label: string;
  /** The RECORD's value, never the change in flight, so a failed save leaves the person's words. */
  value: string;
  placeholder: string | undefined;
  disabled: boolean;
  onCommit: (value: string) => void;
  testId: string;
}

/**
 * A short text setting: local while typing, committed on blur or Enter when it changed. The draft
 * follows the record only when the record moves — a save's answer, or a re-read that found another
 * person's change.
 */
function TextSetting({ label, value, placeholder, disabled, onCommit, testId }: TextSettingProps) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  const commit = () => {
    if (draft === value) return;
    onCommit(draft);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
  };
  return (
    <TextField
      aria-label={label}
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      className="max-w-[420px]"
      testId={testId}
    />
  );
}
